import { L10n } from '~commonInfra/L10n/L10n';
import { Formatters, Messages } from '~src/codegen/Localize';

import { ITaskDetailsResult, ITaskDetailsViewModel } from '../types/ITaskDetails';

const MS_PER_MINUTE = 60000;

/** Localized date-time for an epoch-ms timestamp, or null if absent. */
function formatTimestamp(epochMs: number | null): string | null {
  // Treat any non-finite value (null/undefined/NaN) as absent so the cell shows a dash instead of
  // an Invalid Date; 0 is a real epoch and is kept.
  if (epochMs == null || !Number.isFinite(epochMs)) {
    return null;
  }
  return Formatters.formatMediumDateTimeWithoutSeconds(new Date(epochMs));
}

/**
 * Localized duration label for a millisecond value, expressed in minutes to match the
 * existing Jobs-page convention (`backgroundJobTooltip*` strings use minutes). Null stays
 * null so the renderer shows a dash; 0 is a real value and is preserved.
 */
function formatDurationMinutes(ms: number | null): string | null {
  // Same non-finite guard as formatTimestamp; 0 is a real value and is kept.
  if (ms == null || !Number.isFinite(ms)) {
    return null;
  }
  return Messages.taskDetailsDurationMinutes({ MINUTES: L10n.localizedFloat(ms / MS_PER_MINUTE) });
}

/**
 * Transforms a raw `getTaskDetails` result into the view model consumed by
 * {@link TaskMetadataSection} (S05-b). Pure and null-safe: timestamps/durations become
 * localized strings, content identity is passed through for href construction at render.
 */
export function toTaskDetailsViewModel(result: ITaskDetailsResult): ITaskDetailsViewModel {
  return {
    taskId: result.taskId,
    taskType: result.taskType,
    priority: result.priority,
    site: result.site,
    project: result.project,
    creatorEmail: result.creatorEmail,
    schedule: result.schedule,
    refreshExtractTriggered: result.refreshExtractTriggered,

    contentName: result.contentName,
    contentLuid: result.contentLuid,
    contentId: result.contentId,
    contentType: result.contentType,
    contentOwnerEmail: result.contentOwnerEmail,

    lastSuccessfulRunAt: formatTimestamp(result.lastSuccessfulRunAt),
    nextRunAt: formatTimestamp(result.nextRunAt),
    avgQueueTime: formatDurationMinutes(result.avgQueueTime),
    avgRunTime: formatDurationMinutes(result.avgRunTime),
  };
}
